import { useState } from "react";
import { styled } from "styled-components";
import { useDebounce } from "use-debounce";

import { topicsApiSlice } from "api/topicsApi.slice";
import { AddItemIcon, CrossCircledIcon, InfinityLoader, UIButton } from "common/components";
import { Autocomplete } from "common/components/form/Autocomplete/Autocomplete";
import { TOPICS_FOR_COLLECTION_LIMIT } from "common/constants/limit";
import { createScrollbarStyle } from "common/styles";
import { cssResets } from "common/styles/cssResets";
import type { Setter } from "common/types/setter";
import { Status } from "common/types/status";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import {
  getCollectionForViewing,
  getCollectionForViewingStatus,
  getCollectionForViewingTopicsMeta,
} from "domains/collection/selectors";
import { fetchTopicsForCollection, fetchTopicSuccess } from "entities/topics/actions";
import { createTopicsSelector } from "entities/topics/selectors";

const StyledContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  overflow: clip scroll;

  ${createScrollbarStyle()}
`;

const StyledCard = styled.div`
  background-color: ${({ theme }) => theme.colors.coBrandBackground};
  display: flex;
  gap: 1rem;
  height: calc(7.875rem - 19.5px);
  padding: 1rem;
  width: 20.3125rem;
`;

const StyledImage = styled.img`
  border-radius: 0.25rem;
  height: 3.125rem;
  object-fit: cover;
  object-position: center;
  width: 3.125rem;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledName = styled.p`
  ${cssResets.p};
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  color: ${({ theme }) => theme.colors.textBright};
  display: -webkit-box;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 1.75rem;
`;

const StyledButton = styled(UIButton)`
  background-color: ${({ theme }) => theme.colors.coBrandBackground};
  padding: 0;

  &:focus-visible,
  &:hover {
    background-color: ${({ theme }) => theme.colors.coBrandBackground};
  }
`;

const StyledDeleteIcon = styled(CrossCircledIcon)`
  height: 1rem;
  padding-right: 1rem;
  width: 1rem;
`;

type TopicsTabProps = { setTopicIds: Setter<string[]>; topicIds: string[] };

export const TopicsTab = ({ setTopicIds, topicIds }: TopicsTabProps) => {
  const dispatch = useDispatch();
  const collectionTopics = useMemoisedSelector(() => topicIds, createTopicsSelector);
  const collection = useSelector(getCollectionForViewing);
  const status = useSelector(getCollectionForViewingStatus);
  const topicsMeta = useSelector(getCollectionForViewingTopicsMeta);
  const translate = useTranslate();
  const [filterTerm, setFilterTerm] = useState("");

  const fetchNextPage = () =>
    collection === undefined ||
    topicsMeta === undefined ||
    dispatch(
      fetchTopicsForCollection({ id: collection.id, limit: TOPICS_FOR_COLLECTION_LIMIT, offset: topicsMeta.offset }),
    );

  const collectionTopicsIsPending = status === Status.Loading || topicsMeta?.status !== Status.Ready;

  const [debouncedFilterTerm] = useDebounce(filterTerm, 500);

  const {
    data: paginatedTopicSearchResults,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage: fetchNextResultsPage,
  } = topicsApiSlice.endpoints.searchTopics.useInfiniteQuery({
    search: debouncedFilterTerm,
  });

  const loadNextPage = () => {
    if (!hasNextPage || isFetchingNextPage) return;

    return fetchNextResultsPage();
  };

  const topicSearchResults = paginatedTopicSearchResults?.pages.flat() ?? [];

  return (
    <>
      <Autocomplete
        id="topics-dropdown-select-input"
        value={filterTerm}
        placeholder={translate("app.collection.search_for_topics")}
        isLoading={isLoading}
        options={topicSearchResults.map((item) => ({
          icon: topicIds.some((id) => id === item.id) ? <CrossCircledIcon /> : <AddItemIcon />,
          iconLabel: topicIds.some((id) => id === item.id)
            ? translate("app.create.collection.topics.remove")
            : translate("app.create.collection.topics.add"),
          image: item.image_url,
          id: item.id,
          name: item.name,
          onClick: () => {
            setTopicIds(toggleInclusion(item.id));
            // TODO: Need to add `images` once the API is able to return it
            dispatch(fetchTopicSuccess({ topic: { ...item, images: null } }));
          },
        }))}
        isFetchingNextPage={isFetchingNextPage}
        onChange={setFilterTerm}
        onClear={() => setFilterTerm("")}
        onEndReached={loadNextPage}
      />
      <InfinityLoader
        hasNextPage={status === Status.Loading || (topicsMeta?.nextPageMayExist ?? false)}
        isPending={collectionTopicsIsPending}
        loadNextPage={fetchNextPage}
      >
        <StyledContainer>
          {collectionTopics.map((item) => (
            <CollectionTopicItem
              key={item.id}
              id={item.id}
              name={item.name}
              imageUrl={item.images?.thumbnail ?? item.image_url}
              onRemove={() => setTopicIds((value) => value.filter((id) => id !== item.id))}
            />
          ))}
        </StyledContainer>
      </InfinityLoader>
    </>
  );
};

type CollectionTopicItemProps = {
  id: string;
  imageUrl?: string;
  name: string;
  onRemove: () => void;
};

const CollectionTopicItem = ({ id, imageUrl, name, onRemove }: CollectionTopicItemProps) => {
  const translate = useTranslate();

  return (
    <StyledCard key={id}>
      <StyledImage src={imageUrl} />
      <StyledColumn>
        <StyledName>{name}</StyledName>
        <StyledButtonContainer>
          <StyledButton
            aria-label={`${translate("app.create.collection.topics.remove")} ${name}`}
            onClick={onRemove}
            variant="neutral"
          >
            <StyledDeleteIcon />
            <span>{translate("app.create.collection.topics.remove")}</span>
          </StyledButton>
        </StyledButtonContainer>
      </StyledColumn>
    </StyledCard>
  );
};

const toggleInclusion = (itemId: string) => (ids: string[]) =>
  ids.includes(itemId) ? ids.filter((id) => id !== itemId) : [...ids, itemId];
